import React, { useState, useEffect, useRef } from 'react';
import { Download, Save, FolderOpen, Plus, FileText, AlertTriangle, Package, Upload, Trash2, ChevronDown, Image, Play, MessageSquare, Square, Diamond, Wand2, Copy, Smile } from 'lucide-react';
import SamLogo from '../SamLogo';
// LOCAL MODE (commented out 2025-10-03): saveFile/saveToExistingFile retained for potential re-enable
// import { saveFile, saveToExistingFile } from '../../utils/fileSystemApi';
import { openFile, isFileSystemAccessSupported } from '../../utils/fileSystemApi';
import { stripUIState } from '../../utils/persistenceHelper';
import { buildNormalizedGraph, buildFlowFromGraph, normalizePriority, bfsOrder } from '../../utils/graphNormalize';
import { cleanDanglingEdges } from '../../utils/edgeCleaner';
import { buildDescriptorJSON, deriveMediaRefsFromDescriptor } from '../../utils/buildDescriptorJSON';
import { stampMediaDimensions } from '../../utils/measureMedia';
import { expandPackageNodes } from '../../utils/packageExpansion';
import { derivePackageRefsFromPayload } from '../../utils/packageRefs';
import { createSurveyInCloud, createSurveyVersionInCloud, updateSurveyVersionInCloud, getSurveyVersionFromCloud, getSurveySummaryFromCloud, listSurveysFromCloud, deleteSurveyInCloud, updateSurveyInCloud, copySurveyInCloud, syncMediaReferences, syncPackageReferences } from '../../utils/cloudApi';
import { useAuth } from '../../context/AuthContext';
import CloudTableDialog from '../common/CloudTableDialog';
import { useNotifications } from '../Notifications/NotificationContext';
import { createPackageFromSurvey, savePackageToFile, loadPackageFromFile } from '../../utils/packageManager';
import { validatePackageStructure } from '../../utils/packageValidation';
import { compileSurvey } from '../../utils/compileSurvey';
import PackageMetadataModal from '../Packages/PackageMetadataModal';
import PackagePanel from '../Packages/PackagePanel';
import { suggestInstanceName, isValidInstanceName } from '../../utils/packageInstance';
import { NODE_TYPES, NODE_DEFAULTS } from '../../constants/nodeTypes';
import { reconstructPayloadFromDescriptor } from '../../utils/payloadRecovery';
import { getCanvasCenterPosition, avoidOverlap } from '../../utils/canvasViewport';

// Migration functions for backward compatibility
const detectFileVersion = (surveyData) => {
  // Check for new format indicators
  const hasNewStartEndFormat = surveyData.nodes?.some(node =>
    ['start', 'end'].includes(node.type) &&
    node.data?.question_type &&
    ['introduction', 'completion'].includes(node.data.question_type)
  );

  if (hasNewStartEndFormat) {
    return '1.1'; // New format
  }

  // Check for legacy format
  const hasLegacyStartEnd = surveyData.nodes?.some(node =>
    ['start', 'end'].includes(node.type) &&
    (!node.data || Object.keys(node.data).length === 0)
  );

  if (hasLegacyStartEnd) {
    return '1.0'; // Legacy format
  }

  return '1.1'; // Default to new format
};

const migrateLegacyNodes = (nodes) => {
  return nodes.map(node => {
    if (node.type === 'start' && (!node.data || Object.keys(node.data).length === 0)) {
      return {
        ...node,
        data: { ...NODE_DEFAULTS[NODE_TYPES.START] }
      };
    }

    if (node.type === 'end' && (!node.data || Object.keys(node.data).length === 0)) {
      return {
        ...node,
        data: { ...NODE_DEFAULTS[NODE_TYPES.END] }
      };
    }

    return node;
  });
};

// v5 migration: ensure end node id is explicit (end_*) and not the token "END"
const normalizeEndIds = (nodes, edges) => {
  let changed = false;
  const outNodes = nodes.map(n => ({ ...n, data: { ...(n.data || {}) } }));
  const outEdges = edges.map(e => ({ ...e, data: { ...(e.data || {}) } }));
  for (let i = 0; i < outNodes.length; i++) {
    const n = outNodes[i];
    if (n.type === 'end') {
      const oldId = n.id;
      const dataId = n.data?.id;
      const isLegacy = oldId === 'END' || dataId === 'END' || n.data?.question_id === 'END';
      if (isLegacy) {
        const newId = `end_${Date.now()}`;
        n.id = newId;
        n.data.id = newId;
        if (n.data.question_id === 'END' || !n.data.question_id) n.data.question_id = newId;
        // rewrite edges
        for (let j = 0; j < outEdges.length; j++) {
          if (outEdges[j].source === oldId) outEdges[j].source = newId;
          if (outEdges[j].target === oldId || outEdges[j].target === 'END') outEdges[j].target = newId;
        }
        changed = true;
      }
    }
  }
  return { nodes: outNodes, edges: outEdges, changed };
};

const migrateSurveyData = (legacyData) => {
  return {
    ...legacyData,
    version: "1.1", // Update version
    nodes: migrateLegacyNodes(legacyData.nodes || []),
    // Keep everything else the same
    edges: legacyData.edges,
    survey: legacyData.survey,
    metadata: {
      ...legacyData.metadata,
      migrated: true,
      migratedAt: new Date().toISOString()
    }
  };
};


const Toolbar = ({
  surveyData,
  nodes,
  edges,
  onSurveyDataChange,
  onLoadSurvey,
  onValidateSurvey,
  selectedNode,
  selectedEdge,
  selectionCount = 0,
  onDeleteSelection,
  initialSurveyContext,
  currentSurveyId,
  setCurrentSurveyId,
  currentVersionNumber,
  setCurrentVersionNumber,
  onToggleAssistant,
  // Hands the save machinery up to App, which owns the idle clock and has to
  // rescue unsaved work two minutes before a sign-out. The compile-and-save
  // path lives down here, so App cannot do it alone.
  onExposeSaveApi,
  // Called after a cloud save that actually landed, so App can drop the
  // recovery draft it is holding for this survey.
  onCloudSaved,
}) => {
  const [currentFileHandle, setCurrentFileHandle] = useState(null);
  // LOCAL MODE (commented out 2025-10-03)
  // const [currentFileName, setCurrentFileName] = useState(null);
  // Cloud survey state tracking
  // Now managed via props from App.js
  const [currentEtag, setCurrentEtag] = useState(initialSurveyContext?.etag || null);
  const [currentSurveyName, setCurrentSurveyName] = useState(initialSurveyContext?.surveyName || null);
  const [saveMode, setSaveMode] = useState('overwrite'); // 'overwrite' or 'version'
  const [showSaveDropdown, setShowSaveDropdown] = useState(false);
  const dropdownRef = useRef(null);
  const [isSaving, setIsSaving] = useState(false);
  const [showAddNodeMenu, setShowAddNodeMenu] = useState(false);
  // Survey Packages (WP-5/6/7)
  const [showPackageModal, setShowPackageModal] = useState(false);
  const [packageModalCtx, setPackageModalCtx] = useState({ computed: {}, validation: {} });
  const [packageSaving, setPackageSaving] = useState(false);
  const [showPackagePicker, setShowPackagePicker] = useState(false);
  const [rebindTargetNodeId, setRebindTargetNodeId] = useState(null);
  const [showPackageManager, setShowPackageManager] = useState(false);
  const [pkgReload, setPkgReload] = useState(0);
  const [editMetaRow, setEditMetaRow] = useState(null);
  const [showEditMeta, setShowEditMeta] = useState(false);
  // Whether the open asset is a package ('package') or an ordinary survey.
  // Set when saving-as / opening a package; drives Save-as-version semantics.
  const [currentAssetKind, setCurrentAssetKind] = useState('survey');
  const isSupported = isFileSystemAccessSupported();
  // Ref to always read the latest currentSurveyId value (avoids stale closures)
  const currentSurveyIdRef = useRef(currentSurveyId);
  useEffect(() => { currentSurveyIdRef.current = currentSurveyId; }, [currentSurveyId]);
  const { showSuccess, showError, showWarning } = useNotifications();
  // Authenticated user from Hanko (see src/context/AuthContext.js). The
  // Designer is gated by ProtectedRoute, so `user` is guaranteed non-null
  // here — but defensive ?. access at the call site keeps things safe if
  // the gate is ever loosened.
  const { user } = useAuth();
  // Removed BroadcastChannel/requestFocus: named windows provide reliable reuse

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setShowSaveDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleNewSurvey = () => {
    if (window.confirm('Create a new survey? This will clear the current design.')) {
      setCurrentFileHandle(null);
      // LOCAL MODE (commented out 2025-10-03)
      // setCurrentFileName(null);
      // Clear cloud survey state for new survey
      setCurrentSurveyId(null);
      setCurrentVersionNumber(null);
      setCurrentEtag(null);
      currentSurveyIdRef.current = null; // Immediately update ref to avoid stale closure
      setSaveMode('overwrite');

      // Clear URL params so focus handler doesn't try to reload old survey
      try {
        const url = new URL(window.location.href);
        url.searchParams.delete('surveyId');
        url.searchParams.delete('ver');
        window.history.replaceState(null, '', url.toString());
      } catch (_) { /* noop */ }

      onLoadSurvey({
        survey: {
          title: 'New Survey',
          description: '',
          estimatedDuration: '',
          author: '',
          version: '1.0',
          voice: ''
        },
        nodes: [],
        edges: [],
        // canvas appearance is a user preference now — a new survey doesn't reset it
      });
    }
  };

  const createSurveyData = () => {
    // Strip UI state and then normalize legacy END ids → end_*
    const { nodes: cleanNodes0, edges: cleanEdges0 } = stripUIState(nodes, edges);
    const { nodes: cleanNodes, edges: cleanEdges } = normalizeEndIds(cleanNodes0, cleanEdges0);
    return {
      version: '1.1', // Always save in new format
      metadata: {
        created: new Date().toISOString(),
        tool: 'CloudSam Survey Designer'
      },
      survey: surveyData,
      nodes: cleanNodes,
      edges: cleanEdges,
      // canvas appearance is a user preference — no longer saved in the payload
      layout: {
        zoom: 1,
        position: [0, 0]
      }
    };
  };


  const isCloudMode = true;
  const [openCloudDialog, setOpenCloudDialog] = useState(false);

  const handleSave = async () => {
    // Cloud-only: route all saves through cloud path
    await handleSaveToCloud();
    return;
    /* LOCAL MODE (commented out 2025-10-03)
    if (!isSupported) {
      showError('File System Access API not supported. Please use Chrome or Edge for file management.');
      return;
    }
    // ... local save logic ...
    */
  };

  // LOCAL MODE (commented out 2025-10-03)
  // const handleSaveAs = async () => {
  //   if (isCloudMode) {
  //     await handleSaveToCloud();
  //     return;
  //   }
  //   if (!isSupported) {
  //     showError('File System Access API not supported. Please use Chrome or Edge for file management.');
  //     return;
  //   }
  //
  //   const cleaned = cleanDanglingEdges(nodes, edges);
  //   if (cleaned.removed.length > 0) {
  //     const msg = `Removed ${cleaned.removed.length} dangling edge(s).`;
  //     showWarning(msg);
  //     onLoadSurvey({ survey: surveyData, nodes, edges: cleaned.edgesClean });
  //   }
  //
  //   const surveyDesign = createSurveyData();
  //   // Stage 3: Well-formed validation on Save As
  //   try {
  //     const graph = buildNormalizedGraph(surveyDesign.nodes || [], surveyDesign.edges || []);
  //     const flowNormalized = buildFlowFromGraph({ edgesClean: graph.edgesClean, startNodeId: graph.startNodeId });
  //     const wfSurvey = {
  //       survey: surveyData,
  //       questions: (surveyDesign.nodes || []).filter(n => ['question','start','end'].includes(n.type)).map(n => ({
  //         question_id: n.id,
  //         question_text: n.data?.text || n.data?.question_text || '',
  //         question_type: n.type === 'start' ? 'introduction' : (n.type === 'end' ? 'completion' : (n.data?.type || n.data?.question_type || 'text')),
  //         variable_name: n.data?.variable || n.data?.variable_name || null,
  //         required: n.data?.required !== false,
  //         category: n.data?.category || 'general'
  //       })),
  //       flow: flowNormalized
  //     };
  //     const issues = runWellFormed(wfSurvey) || [];
  //     const errs = issues.filter(i => i.level !== 'warning');
  //     const warns = issues.filter(i => i.level === 'warning');
  //     if (errs.length > 0) {
  //       showError(`Cannot save: ${errs.length} well-formed error(s). Fix and try again.`);
  //       return;
  //     }
  //     if (warns.length > 0) showWarning(`${warns.length} well-formed warning(s). Save allowed.`);
  //   } catch (_) { /* silent */ }
  //   // LOCAL MODE (commented out 2025-10-03)
  //   // const suggestedName = `${surveyData.title?.replace(/[^a-z0-9]/gi, '_').toLowerCase() || 'survey'}.cloudsam`;
  //   // try {
  //   //   const fileHandle = await saveFile(surveyDesign, suggestedName);
  //   //   if (fileHandle) {
  //   //     setCurrentFileHandle(fileHandle);
  //   //     setCurrentFileName(fileHandle.name);
  //   //     showSuccess('File saved successfully!');
  //   //   }
  //   // } catch (error) {
  //   //   showError('Error saving file: ' + error.message);
  //   // }
  // };

  /**
   * Save to the cloud.
   *
   * @param {object} [opts]
   * @param {'overwrite'|'version'} [opts.mode]  overrides the toolbar's setting.
   * @param {string} [opts.notes]  what to record against the version. The idle
   *   auto-save passes its own text, so an author coming back to an extra
   *   version can see why it appeared.
   * @returns {Promise<{ok: boolean, surveyId?, versionNumber?, reason?, error?}>}
   *   It reports rather than throws, because the author-facing path shows its
   *   own message. The idle auto-save needs the answer to know whether it can
   *   truthfully say the work was saved.
   */
  const handleSaveToCloud = async ({ mode, notes } = {}) => {
    if (isSaving) return { ok: false, reason: 'busy' }; // guard against re-entry
    setIsSaving(true);
    const cleanedCloud = cleanDanglingEdges(nodes, edges);
    if (cleanedCloud.removed.length > 0) {
      const msg = `Removed ${cleanedCloud.removed.length} dangling edge(s).`;
      showWarning(msg);
      onLoadSurvey({ survey: surveyData, nodes, edges: cleanedCloud.edgesClean });
    }

    try {
      const payload = createSurveyData();
      // Priority coercion warnings removed by policy: non-numeric priorities are treated as 0 silently
      // Remove all blocking structural checks for cloud save; rely on cleaner and rebuild

      // WP-2: expand embedded packages BEFORE the descriptor/flow build. The
      // saved designer_payload keeps the 📦 nodes intact (payload is untouched);
      // only the compiled descriptor sees the expanded, namespaced contents.
      const compiled = expandPackageNodes(payload.nodes || [], payload.edges || []);

      // Build normalized graph/flow (Stage 2) from the EXPANDED graph
      const graph = buildNormalizedGraph(compiled.nodes, compiled.edges);
      let flowNormalized = buildFlowFromGraph({ edgesClean: graph.edgesClean, startNodeId: graph.startNodeId });

      // Remap any START→X edges to startId→X and do not insert START→start
      try {
        const startId = graph.startNodeId;
        const remapped = flowNormalized.map(e => (e.from === 'START' ? { ...e, from: startId } : e));
        flowNormalized = remapped;
      } catch (_) { /* noop */ }

      const flowSorted = [...flowNormalized].sort((a, b) => {
        if (a.from < b.from) return -1;
        if (a.from > b.from) return 1;
        const pa = normalizePriority(a.priority), pb = normalizePriority(b.priority);
        if (pa !== pb) return pb - pa; // higher priority first
        if (a.to < b.to) return -1;
        if (a.to > b.to) return 1;
        return 0;
      });

      // Compute BFS order for stable questions[] ordering
      const bfsIds = bfsOrder(graph.startNodeId, graph.adjacency);
      const bfsPos = new Map(bfsIds.map((id, idx) => [id, idx]));
      const sortedQuestionNodes = compiled.nodes
        .filter(n => ['question', 'start', 'end', 'decision', 'media', 'pleasant'].includes(n.type))
        .sort((a, b) => {
          const ia = bfsPos.has(a.id) ? bfsPos.get(a.id) : Number.MAX_SAFE_INTEGER;
          const ib = bfsPos.has(b.id) ? bfsPos.get(b.id) : Number.MAX_SAFE_INTEGER;
          if (ia !== ib) return ia - ib;
          return String(a.id).localeCompare(String(b.id));
        });

      // Stamp media nodes with their images' natural dimensions before the
      // descriptor is built — the presentor reads them to pick a layout the
      // moment a stimulus arrives, rather than waiting on the image to load.
      const measuredQuestionNodes = await stampMediaDimensions(sortedQuestionNodes);

      const descriptor = buildDescriptorJSON({
        sortedQuestionNodes: measuredQuestionNodes,
        edges: compiled.edges,
        surveyData: payload.survey || {},
        flowSorted
      });

      // No well-formed validation prior to cloud save

      const designerGraph = {
        nodes: (payload.nodes || []).map(n => ({ id: n.id, position: n.position })),
        edges: (graph.edgesClean || []).map(e => ({ id: e.id, source: e.source, target: e.target }))
      };

      // Use ref to get the latest surveyId (avoids stale closure after New Survey)
      const latestSurveyId = currentSurveyIdRef.current;
      let saveResult;
      if (!latestSurveyId) {
        // New survey - create it (always creates new survey)
        saveResult = await handleSaveNewSurvey(descriptor, designerGraph, payload);
      } else if ((mode || saveMode) === 'version') {
        // Create new version of existing survey
        saveResult = await handleSaveNewVersion(descriptor, designerGraph, payload, notes);
      } else {
        // Overwrite current version (default)
        saveResult = await handleSaveOverwrite(descriptor, designerGraph, payload, notes);
      }

      if (saveResult && saveResult.surveyId && saveResult.versionNumber) {
        // WP-2: derive media refs from the EXPANDED descriptor rather than the
        // raw node list, so media nodes living inside embedded packages are seen
        // (a top-level node walk cannot see through a 📦). Behaviour-identical to
        // the old walk for package-free surveys.
        // Best-effort, like the package-ref sync below it. The version is
        // already written at this point, so a ref-sync failure must not be
        // reported as "Cloud save failed" — that sends the author back to
        // re-save work that is already saved, which is how an expired session
        // turned into an unsaveable survey.
        try {
          const refs = deriveMediaRefsFromDescriptor(descriptor);
          await syncMediaReferences({
            surveyId: saveResult.surveyId,
            versionNumber: saveResult.versionNumber,
            refs
          });
        } catch (mediaErr) {
          showWarning(`Saved, but media links did not sync: ${mediaErr.message}`);
        }

        // WP-4: register embedded-package refs from the AUTHORED payload (the
        // 📦 nodes, not the expanded descriptor) so the reverse-lookup registry
        // stays current. Best-effort — a registry hiccup must not fail the save.
        try {
          const pkgRefs = derivePackageRefsFromPayload(payload?.nodes || []);
          await syncPackageReferences({
            surveyId: saveResult.surveyId,
            versionNumber: saveResult.versionNumber,
            refs: pkgRefs
          });
        } catch (pkgErr) {
          showWarning(`Saved, but package links did not sync: ${pkgErr.message}`);
        }
      }
      // A real save means the recovery draft has done its job. App clears it,
      // so the author is not offered stale work the next time they open this
      // survey. Only a save that got this far counts.
      try {
        if (onCloudSaved) onCloudSaved({ auto: Boolean(notes), surveyId: saveResult?.surveyId || null });
      } catch (savedErr) {
        // Clearing a draft is housekeeping. It must never turn a good save
        // into a reported failure.
      }
      return {
        ok: true,
        surveyId: saveResult?.surveyId || null,
        versionNumber: saveResult?.versionNumber || null,
      };
    } catch (err) {
      showError(`Cloud save failed: ${err.message}`);
      return { ok: false, error: err.message };
    } finally {
      setIsSaving(false);
    }
  };

  const handleSaveNewSurvey = async (descriptor, designerGraph, payload) => {
    const name = payload.survey?.title || `survey_${Date.now()}`;
    const created = await createSurveyInCloud({
      name,
      descriptor,
      initialStatus: 'draft',
      designerGraph,
      designerPayload: payload,
      // Ownership: prefer org_id when the author has one (team survey), fall
      // back to user_id for personal surveys. dbam's CreateSurvey validates
      // "exactly one of UserID or OrgID" and 400s otherwise.
      userId: user?.user_id,
      orgId: user?.org_id || null,
    });
    const surveyId = created?.survey_id || created?.id || created?.survey?.id;
    if (!surveyId) throw new Error('Cloud did not return survey_id');

    // Set the survey state for future saves
    setCurrentSurveyId(surveyId);
    setCurrentVersionNumber(1);
    setCurrentEtag(null);
    setCurrentSurveyName(String(name).trim());

    showSuccess(`Created new survey ${surveyId}`);
    return { surveyId, versionNumber: 1 };
  };

  // `notes` is what the author sees against the version later. The idle
  // auto-save passes its own text so an extra version is never a mystery.
  const handleSaveNewVersion = async (descriptor, designerGraph, payload, notes) => {
    const versionResult = await createSurveyVersionInCloud({
      surveyId: currentSurveyId,
      descriptor,
      status: 'draft',
      publish: false,
      notes: notes || 'New version from SD_Local',
      designerGraph,
      designerPayload: payload
    });
    const newVersionNumber = versionResult?.version_number || (currentVersionNumber + 1);

    setCurrentVersionNumber(newVersionNumber);
    setCurrentEtag(null); // Reset etag for new version
    // Keep mutable survey metadata synchronized with the saved version. Console
    // uses this list for discovery only; deployment pins the descriptor plan.
    try {
      const desiredName = String(payload?.survey?.title || '').trim();
      const updates = { target_channels: descriptor?.survey_descriptor?.target_channels || ['browser'] };
      if (desiredName && desiredName !== (currentSurveyName || '')) Object.assign(updates, { title: desiredName, name: desiredName });
      if (currentSurveyId) await updateSurveyInCloud({ surveyId: currentSurveyId, updates });
      if (updates.title) setCurrentSurveyName(desiredName);
    } catch (renameErr) {
      showWarning(`Saved version but survey metadata sync failed: ${renameErr.message}`);
    }

    showSuccess(`Created new version ${newVersionNumber} of survey ${currentSurveyId}`);
    return { surveyId: currentSurveyId, versionNumber: newVersionNumber };
  };

  const handleSaveOverwrite = async (descriptor, designerGraph, payload, notes) => {
    const versionNumber = currentVersionNumber || 1;  // Default to 1 if null

    const versionResult = await updateSurveyVersionInCloud({
      surveyId: currentSurveyId,
      versionNumber,
      descriptor,
      status: 'draft',
      notes: notes || 'Updated from SD_Local',
      designerPayload: payload,
      etag: currentEtag
    });

    // Update etag from response for future updates
    setCurrentEtag(versionResult?.etag || null);
    // Keep mutable survey metadata synchronized with the saved version. Console
    // uses this list for discovery only; deployment pins the descriptor plan.
    try {
      const desiredName = String(payload?.survey?.title || '').trim();
      const updates = { target_channels: descriptor?.survey_descriptor?.target_channels || ['browser'] };
      if (desiredName && desiredName !== (currentSurveyName || '')) Object.assign(updates, { title: desiredName, name: desiredName });
      if (currentSurveyId) await updateSurveyInCloud({ surveyId: currentSurveyId, updates });
      if (updates.title) setCurrentSurveyName(desiredName);
    } catch (renameErr) {
      showWarning(`Saved version but survey metadata sync failed: ${renameErr.message}`);
    }

    showSuccess(`Updated version ${versionNumber} of survey ${currentSurveyId}`);
    return { surveyId: currentSurveyId, versionNumber };
  };

  const handleLoad = async () => {
    if (isCloudMode) {
      setOpenCloudDialog(true);
      return;
    }
    if (!isSupported) {
      showError('File System Access API not supported. Please use Chrome or Edge for file management.');
      return;
    }

    try {
      const result = await openFile();
      if (result) {
        const { data, fileHandle } = result; // LOCAL MODE: fileName commented out 2025-10-03
        setCurrentFileHandle(fileHandle);
        // setCurrentFileName(fileName);

        // Detect file version and migrate if needed
        const version = detectFileVersion(data);
        let processedData = data;
        let migrationMessage = '';

        if (version === '1.0') {
          console.log('🔄 Migrating legacy survey format to v1.1');
          processedData = migrateSurveyData(data);
          migrationMessage = ' (upgraded to support Start/End node spoken text)';
        }

        // Apply existing clarificationPolicy migration
        const loadedNodes = (processedData.nodes || []).map(n => {
          let pol = (n.data || {}).clarificationPolicy;
          if (pol === 'always') pol = 'auto';
          n.data = {
            clarificationPolicy: pol || 'auto',
            ...n.data
          };
          return n;
        });

        onLoadSurvey({
          survey: processedData.survey || {},
          nodes: loadedNodes,
          edges: processedData.edges || []
          // any canvas key in older payloads is ignored (user preference now)
        });

        // LOCAL MODE (commented out 2025-10-03): fileName usage removed
        const successMessage = `File loaded successfully!${migrationMessage}`;
        showSuccess(successMessage);

        // Show migration notice if applicable
        if (version === '1.0') {
          setTimeout(() => {
            showWarning('This survey has been upgraded to support spoken text in Start and End nodes. You can now customize what Sam says when beginning and completing surveys.');
          }, 1000);
        }
      }
    } catch (error) {
      showError('Error loading file: ' + error.message);
    }
  };

  const handleExport = async () => {
    // WP-2: expand embedded packages before any compile step, so exported
    // descriptors include package contents (namespaced) just like cloud save.
    // Package-free surveys are unaffected (same references returned).
    const { nodes: expNodes, edges: expEdges } = expandPackageNodes(nodes, edges);

    const cleanedExport = cleanDanglingEdges(expNodes, expEdges);
    if (cleanedExport.removed.length > 0) {
      showWarning(`Removed ${cleanedExport.removed.length} dangling edge(s).`);
    }

    // Convert to CloudSam-compatible format (v5):
    // - question_id uses the node.id for all nodes (start/end/question)
    // - start node question_type is 'introduction'; end node is 'completion'
    // - flow.from uses 'START' token when sourced from the start node; no 'END' token anywhere
    // Stage 2: Normalize legacy END tokens in local export to avoid dropping edges
    const normalizedForExport = normalizeEndIds(expNodes, expEdges);
    // Enforce exactly one Start and one End for local export (parity with cloud save)
    const startNodesListLocal = (normalizedForExport.nodes || []).filter(n => n.type === 'start');
    if (startNodesListLocal.length !== 1) {
      showError('Export blocked: Exactly one Start node is required.');
      return;
    }
    const endNodesListLocal = (normalizedForExport.nodes || []).filter(n => n.type === 'end');
    if (endNodesListLocal.length !== 1) {
      showError('Export blocked: Exactly one End node is required.');
      return;
    }
    const graph = buildNormalizedGraph(normalizedForExport.nodes, cleanedExport.edgesClean);
    // Priority coercion warnings removed by policy: non-numeric priorities are treated as 0 silently
    // Build normalized flow and then canonicalize for export
    const flowNormalized = buildFlowFromGraph({ edgesClean: graph.edgesClean, startNodeId: graph.startNodeId });

    // Helper: map yes/no and legacy labels to canonical string comparisons
    const nodeById = new Map(expNodes.map(n => [n.id, n]));
    const allNodeIds = new Set(expNodes.map(n => n.id));
    const startId = graph.startNodeId;

    let flowCanonical = flowNormalized
      // Drop edges to non-existent nodes (except START and end_*)
      .filter(e => (e.from === 'START' || allNodeIds.has(e.from)) && (allNodeIds.has(e.to) || /^end[_-]/i.test(String(e.to || ''))))
      .map(e => {
        // Resolve effective source node (START maps to startId)
        const srcId = (e.from === 'START') ? startId : e.from;
        const srcNode = nodeById.get(srcId);
        const varName = srcNode && srcNode.data && (srcNode.data.variable || srcNode.data.variable_name);
        const qType = (srcNode && (srcNode.data?.type || srcNode.data?.question_type)) || '';
        let cond = String(e.condition || '').trim();
        if (/^unconditional$/i.test(cond)) cond = '';
        // Canonicalize yes/no
        if (String(qType).toLowerCase() === 'yes_no' && varName) {
          if (/==\s*true\b/i.test(cond)) cond = `${varName} == "yes"`;
          else if (/==\s*false\b/i.test(cond)) cond = `${varName} == "no"`;
          else if (/^(yes|if\s+yes)$/i.test(cond)) cond = `${varName} == "yes"`;
          else if (/^(no|if\s+no)$/i.test(cond)) cond = `${varName} == "no"`;
        }
        return { from: e.from, to: e.to, condition: cond, priority: e.priority };
      });

    // Remap any START→X edges to startId→X for exported flow
    try {
      const startId = graph.startNodeId;
      flowCanonical = flowCanonical.map(e => (e.from === 'START' ? { ...e, from: startId } : e));
    } catch (_) { }

    const flowSorted = [...flowCanonical].sort((a, b) => {
      if (a.from < b.from) return -1;
      if (a.from > b.from) return 1;
      const pa = normalizePriority(a.priority), pb = normalizePriority(b.priority);
      if (pa !== pb) return pb - pa;
      if (a.to < b.to) return -1;
      if (a.to > b.to) return 1;
      return 0;
    });

    // Compute BFS order for stable questions[] ordering
    const bfsIds = bfsOrder(graph.startNodeId, graph.adjacency);
    const bfsPos = new Map(bfsIds.map((id, idx) => [id, idx]));
    const sortedQuestionNodes = expNodes
      .filter(node => ['question', 'start', 'end', 'decision', 'media', 'pleasant'].includes(node.type))
      .sort((a, b) => {
        const ia = bfsPos.has(a.id) ? bfsPos.get(a.id) : Number.MAX_SAFE_INTEGER;
        const ib = bfsPos.has(b.id) ? bfsPos.get(b.id) : Number.MAX_SAFE_INTEGER;
        if (ia !== ib) return ia - ib;
        return String(a.id).localeCompare(String(b.id));
      });

    // Same media measuring pass as Save to Cloud — an exported descriptor has
    // to carry the dimensions too, or a survey round-tripped through a file
    // reaches the presentor without them.
    const measuredQuestionNodes = await stampMediaDimensions(sortedQuestionNodes);

    const exportData = buildDescriptorJSON({
      sortedQuestionNodes: measuredQuestionNodes,
      edges: expEdges,
      surveyData,
      flowSorted
    });

    // No well-formed validation on Export; rely on cleaner and normalized flow

    const blob = new Blob([JSON.stringify(exportData, null, 2)], {
      type: 'application/json'
    });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${surveyData.title?.replace(/[^a-z0-9]/gi, '_').toLowerCase() || 'survey'}_export.json`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };









  const handleValidate = () => {
    const questionNodes = nodes.filter(node => node.type === 'question');
    if (questionNodes.length === 0) {
      showWarning('Add some questions first!');
      return;
    }

    const validationResults = onValidateSurvey();
    const allIssues = Object.values(validationResults).flatMap(v => v.issues);
    const errors = allIssues.filter(i => i.severity === 'error');
    const warnings = allIssues.filter(i => i.severity === 'warning');

    // Validate is advisory. It never blocks a save — it tells the author, in
    // one plain sentence, why the survey would not run if it were deployed.
    if (errors.length > 0) {
      const more = errors.length > 1 ? ` And ${errors.length - 1} more problem(s).` : '';
      showError(`${errors[0].message}${more} You can still save it — see the Flow Validation panel to fix.`);
    } else if (warnings.length > 0) {
      showWarning(`This survey will run, but there are ${warnings.length} thing(s) to check. See the Flow Validation panel.`);
    } else {
      showSuccess(`Validation passed. This survey is connected and ready to run.`);
    }
  };

  // Save as Package (cloud, WP-5). Validate §3.9 → collect metadata → create a
  // kind='package' survey row whose descriptor is the package's own compiled
  // form and whose designer_payload is the graph. The old .sampkg file-download
  // path is gone (export survives only via WP-7's management mode).
  const handleSaveAsPackage = () => {
    setShowSaveDropdown(false);
    const validation = validatePackageStructure(nodes, edges);
    if (!validation.isValid) {
      showError(`Cannot save as package:\n• ${validation.errors.join('\n• ')}`);
      return;
    }
    const variableNames = nodes
      .filter(n => ['text', 'number', 'choice', 'yes_no', 'scale', 'question'].includes(n.type) && (n.data?.variable || n.data?.variable_name))
      .map(n => String(n.data.variable || n.data.variable_name).trim());
    const questionCount = nodes.filter(n => n.type === 'question').length;
    setPackageModalCtx({
      computed: { questionCount, variableNames: [...new Set(variableNames)] },
      validation: { warnings: validation.warnings, notices: validation.notices },
    });
    setShowPackageModal(true);
  };

  const handleConfirmSavePackage = async ({ title, description, tags }) => {
    setPackageSaving(true);
    try {
      const pkgSurvey = {
        ...surveyData,
        title,
        description,
        settings: {
          ...(surveyData.settings || {}),
          questionCount: packageModalCtx.computed.questionCount,
          variables: packageModalCtx.computed.variableNames,
          tags,
        },
      };
      // A package is a valid standalone survey — compile its own descriptor.
      const { descriptor, designerGraph } = compileSurvey({ nodes, edges, surveyData: pkgSurvey });
      const payload = { ...createSurveyData(), survey: pkgSurvey };

      const created = await createSurveyInCloud({
        name: title,
        descriptor,
        initialStatus: 'draft',
        designerGraph,
        designerPayload: payload,
        userId: user?.user_id,
        orgId: user?.org_id || null,
        kind: 'package',
        settings: {
          questionCount: packageModalCtx.computed.questionCount,
          variables: packageModalCtx.computed.variableNames,
          tags,
        },
        tags,
      });
      const packageId = created?.survey_id || created?.id || created?.survey?.id;
      if (!packageId) throw new Error('Cloud did not return a package id');

      // §3.8: anchor the package's own media against GC even before it is
      // embedded anywhere — register refs against the package's own row/version.
      try {
        const refs = deriveMediaRefsFromDescriptor(descriptor);
        await syncMediaReferences({ surveyId: packageId, versionNumber: 1, refs });
      } catch (mErr) {
        showWarning(`Package saved, but media links did not sync: ${mErr.message}`);
      }

      // The designer is now editing this package (subsequent saves version it).
      setCurrentSurveyId(packageId);
      setCurrentVersionNumber(1);
      setCurrentEtag(null);
      setCurrentSurveyName(String(title).trim());
      setCurrentAssetKind('package');
      setSaveMode('overwrite');
      onSurveyDataChange?.({ title, description });

      setShowPackageModal(false);
      showSuccess(`Saved package "${title}"`);
    } catch (err) {
      showError(`Save as package failed: ${err.message}`);
    } finally {
      setPackageSaving(false);
    }
  };

  // Build the C1 `data` for a bound package from a library row (fetches the
  // package's latest version and deep-clones its graph into packageContent).
  const buildBoundPackageData = async (row) => {
    const latestVer = row.version || 1;
    const { data } = await getSurveyVersionFromCloud(row.id, latestVer);
    const versionData = data?.data || data || {};
    const payload = versionData.designer_payload_json;
    if (!payload || typeof payload !== 'object' || !Array.isArray(payload.nodes)) {
      throw new Error('Package has no editable graph (designer_payload missing)');
    }
    const content = {
      nodes: JSON.parse(JSON.stringify(payload.nodes)),
      edges: JSON.parse(JSON.stringify(payload.edges || [])),
    };
    const settings = payload.survey?.settings || {};
    const variables = Array.isArray(settings.variables)
      ? settings.variables
      : content.nodes.filter(n => ['text', 'number', 'choice', 'yes_no', 'scale', 'question'].includes(n.type) && (n.data?.variable || n.data?.variable_name)).map(n => String(n.data.variable || n.data.variable_name).trim());
    const questionCount = typeof settings.questionCount === 'number'
      ? settings.questionCount
      : content.nodes.filter(n => n.type === 'question').length;
    return {
      packageId: row.id,
      packageVersion: latestVer,
      title: row.title || row.name || 'Package',
      description: row.description || '',
      version: latestVer,
      questionCount,
      variables,
      packageContent: content,
      isLoadedPackage: true,
    };
  };

  // Bind a library package as a NEW 📦 node (WP-6). Selecting IS the binding.
  const handleBindPackage = async (row) => {
    try {
      const data = await buildBoundPackageData(row);
      const instanceName = suggestInstanceName(row.title || row.name || 'package', nodes);
      const pkgNode = {
        id: `pkg_${Date.now()}`,
        type: 'package',
        position: avoidOverlap(getCanvasCenterPosition(), nodes),
        data: { instanceName, ...data },
      };
      onLoadSurvey({ survey: surveyData, nodes: [...nodes, pkgNode], edges });
      setShowPackagePicker(false);
      showSuccess(`Inserted package "${data.title}" as ${instanceName}`);
    } catch (err) {
      showError(`Insert package failed: ${err.message}`);
    }
  };

  // Rebind an existing 📦 node to a (possibly different) package (WP-6 item 4):
  // replace packageContent + identity, keep instanceName and the node's edges.
  const handleRebindPackage = async (nodeId, row) => {
    try {
      const data = await buildBoundPackageData(row);
      const updated = nodes.map(n =>
        n.id === nodeId ? { ...n, data: { ...n.data, ...data, instanceName: n.data.instanceName } } : n
      );
      onLoadSurvey({ survey: surveyData, nodes: updated, edges });
      setShowPackagePicker(false);
      setRebindTargetNodeId(null);
      showSuccess(`Rebound to "${data.title}" v${data.packageVersion}`);
    } catch (err) {
      showError(`Rebind failed: ${err.message}`);
    }
  };

  // Refresh a 📦 node to the latest version of its own package (WP-6 item 7).
  const handleRefreshPackage = async (nodeId) => {
    try {
      const node = nodes.find(n => n.id === nodeId);
      if (!node?.data?.packageId) { showWarning('This package has no cloud link to refresh.'); return; }
      const summary = await getSurveySummaryFromCloud(node.data.packageId);
      const row = { id: node.data.packageId, version: summary?.version || node.data.packageVersion || 1, title: summary?.title, description: summary?.description };
      await handleRebindPackage(nodeId, row);
    } catch (err) {
      showError(`Refresh failed: ${err.message}`);
    }
  };

  // Property-panel actions arrive as window events (NodeEditor has no direct
  // handle on the picker). Re-register on `nodes` so refresh sees the current graph.
  useEffect(() => {
    const onRebind = (e) => {
      const id = e?.detail?.nodeId;
      if (!id) return;
      setRebindTargetNodeId(id);
      setShowPackagePicker(true);
    };
    const onRefresh = (e) => {
      const id = e?.detail?.nodeId;
      if (id) handleRefreshPackage(id);
    };
    window.addEventListener('package-rebind-request', onRebind);
    window.addEventListener('package-refresh-request', onRefresh);
    return () => {
      window.removeEventListener('package-rebind-request', onRebind);
      window.removeEventListener('package-refresh-request', onRefresh);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [nodes]);

  // ── WP-7 management handlers (authored by gemini-worker, QA'd) ─────────────
  const handlePackageOpenForEdit = async (row) => {
    try {
      const latestVer = row.version || 1;
      const { data, etag } = await getSurveyVersionFromCloud(row.id, latestVer);
      const versionData = data?.data || data || {};
      let payload = versionData.designer_payload_json;
      if (!payload || typeof payload !== 'object') {
        payload = reconstructPayloadFromDescriptor(versionData.descriptor_json, row.name);
      }
      if (!payload) {
        throw new Error('Could not load or reconstruct package payload.');
      }
      onLoadSurvey({
        survey: payload.survey || {},
        nodes: payload.nodes || [],
        edges: payload.edges || []
      });
      setCurrentSurveyId(row.id);
      setCurrentVersionNumber(latestVer);
      setCurrentEtag(etag);
      setCurrentSurveyName(row.title || row.name || null);
      setCurrentAssetKind('package');
      setSaveMode('overwrite');
      setShowPackageManager(false);
      showSuccess(`Opened package "${row.title || row.name || ''}" for editing`);
    } catch (err) {
      showError(err.message || err);
    }
  };

  const handlePackageDelete = async (row, embedders) => {
    try {
      let msg = '';
      if (embedders && embedders.length > 0) {
        const names = embedders.map(e => e.name).join(', ');
        msg = `This package is embedded in ${embedders.length} survey(s): ${names}. Embedded copies stay intact. Delete anyway?`;
      } else {
        msg = `Delete package "${row.title || row.name || ''}"?`;
      }
      if (!window.confirm(msg)) {
        return;
      }
      await deleteSurveyInCloud({ surveyId: row.id });
      showSuccess('Package deleted successfully');
      setPkgReload(x => x + 1);
    } catch (err) {
      showError(err.message || err);
    }
  };

  const handlePackageEditMeta = (row) => {
    setEditMetaRow(row);
    setShowEditMeta(true);
  };

  const handleConfirmEditMeta = async ({ title, description, tags }) => {
    try {
      await updateSurveyInCloud({
        surveyId: editMetaRow.id,
        updates: { title, description, tags }
      });
      setShowEditMeta(false);
      setPkgReload(x => x + 1);
      showSuccess('Package updated');
    } catch (err) {
      showError(err.message || err);
    }
  };

  const handlePackageExport = async (row) => {
    try {
      const latestVer = row.version || 1;
      const { data } = await getSurveyVersionFromCloud(row.id, latestVer);
      const versionData = data?.data || data;
      const payload = versionData?.designer_payload_json;
      if (!payload || !payload.nodes) {
        throw new Error('no editable graph');
      }
      const packageInfo = {
        id: (row.title || row.name || 'package').toLowerCase().replace(/[^a-z0-9]/g, '-'),
        title: row.title || row.name,
        description: row.description || '',
        author: '',
        version: String(row.version || 1)
      };
      const packageData = createPackageFromSurvey(
        payload.survey || {},
        payload.nodes || [],
        payload.edges || [],
        packageInfo
      );
      const result = await savePackageToFile(packageData, packageInfo.id);
      if (result.success) {
        showSuccess('Package exported successfully');
      } else if (result.error && !/abort/i.test(result.error)) {
        showError(result.error);
      }
    } catch (err) {
      if (err?.name !== 'AbortError') {
        showError(err.message || err);
      }
    }
  };

  const handlePackageImport = async () => {
    try {
      const handles = await window.showOpenFilePicker({
        types: [{
          description: 'Survey Assistant Module Package',
          accept: { 'application/json': ['.sampkg'] }
        }]
      });
      if (!handles || !handles.length) return;
      const result = await loadPackageFromFile(handles[0]);
      if (!result.success) {
        showError(`Import failed: ${result.error}`);
        return;
      }
      const pkg = result.data;
      const nodes = pkg.surveyData?.nodes || [];
      const edges = pkg.surveyData?.edges || [];
      const validation = validatePackageStructure(nodes, edges);
      if (!validation.isValid) {
        showError(`Imported package fails validation:\n• ${validation.errors.join('\n• ')}`);
        return;
      }
      // media dangling check: count media display nodes referencing an asset
      const mediaRefs = nodes.filter(n => (n.type === 'media') && (n.data?.show !== false) && (n.data?.media?.src || n.data?.media?.asset_id));
      if (mediaRefs.length) {
        showWarning(`This package references ${mediaRefs.length} media asset(s). If it was authored in another installation, those may not display until re-linked.`);
      }
      const surveyMeta = {
        title: pkg.packageInfo?.title || pkg.surveyData?.title || 'Imported Package',
        description: pkg.packageInfo?.description || ''
      };
      const { descriptor, designerGraph } = compileSurvey({ nodes, edges, surveyData: surveyMeta });
      const designerPayload = {
        version: '1.1',
        survey: {
          ...surveyMeta,
          settings: {
            questionCount: nodes.filter(n => n.type === 'question').length,
            variables: pkg.packageInfo?.variables || []
          }
        },
        nodes,
        edges
      };
      const created = await createSurveyInCloud({
        name: surveyMeta.title,
        descriptor,
        initialStatus: 'draft',
        designerGraph,
        designerPayload,
        userId: user?.user_id,
        orgId: user?.org_id || null,
        kind: 'package',
        settings: designerPayload.survey.settings,
        tags: []
      });
      const pkgId = created?.survey_id || created?.id;
      if (pkgId) {
        try {
          await syncMediaReferences({
            surveyId: pkgId,
            versionNumber: 1,
            refs: deriveMediaRefsFromDescriptor(descriptor)
          });
        } catch (_) {}
      }
      setPkgReload(x => x + 1);
      showSuccess(`Imported package "${surveyMeta.title}"`);
    } catch (err) {
      if (err?.name !== 'AbortError') {
        showError(`Import failed: ${err.message}`);
      }
    }
  };



  const createPackageNode = (packageData) => {
    // Calculate position for new package node
    const existingNodes = nodes;
    const maxX = existingNodes.length > 0 ? Math.max(...existingNodes.map(n => n.position.x)) : 0;
    const maxY = existingNodes.length > 0 ? Math.max(...existingNodes.map(n => n.position.y)) : 0;
    const position = { x: maxX + 250, y: Math.max(100, maxY) };

    // Create single package node with encapsulated content
    const packageNode = {
      id: `package-${Date.now()}`,
      type: 'package',
      position,
      data: {
        id: packageData.packageInfo.id,
        title: packageData.packageInfo.title,
        description: packageData.packageInfo.description,
        version: packageData.packageInfo.version,
        questionCount: packageData.packageInfo.questionCount,
        variables: packageData.packageInfo.variables,
        // Store the package content internally (not expanded)
        packageContent: {
          nodes: packageData.surveyData.nodes,
          edges: packageData.surveyData.edges
        },
        // Mark as loaded package
        isLoadedPackage: true
      }
    };

    // Add only the single package node to the survey
    onLoadSurvey({
      survey: surveyData,
      nodes: [...nodes, packageNode],
      edges: edges
    });
  };

  const addQuickNode = (nodeType) => {
    // Drop the new node in the middle of whatever the user is looking at,
    // nudging it clear if something is already parked there.
    const position = avoidOverlap(getCanvasCenterPosition(), nodes);

    let newNode;
    switch (nodeType) {
      case 'start':
        newNode = {
          id: `start_${Date.now()}`,
          type: 'start',
          position,
          data: { ...NODE_DEFAULTS[NODE_TYPES.START] }
        };
        break;
      case 'end':
        newNode = {
          id: `end_${Date.now()}`,
          type: 'end',
          position,
          data: { ...NODE_DEFAULTS[NODE_TYPES.END], id: `end_${Date.now()}` }
        };
        break;
      case 'question':
        const placeholderId = `Q${nodes.filter(n => n.type === 'question').length + 1}`;
        newNode = {
          id: `q_${Date.now()}`,
          type: 'question',
          position,
          data: {
            id: placeholderId,
            text: 'New Question',
            type: 'text',
            variable: `q_${placeholderId.toLowerCase()}`,
            required: true,
            options: [],
            clarificationPolicy: 'auto'
          }
        };
        break;

      case 'decision':
        newNode = {
          id: `decision_${Date.now()}`,
          type: 'decision',
          position,
          data: {
            ...NODE_DEFAULTS[NODE_TYPES.DECISION],
            id: `D${nodes.filter(n => n.type === 'decision').length + 1}`,
          }
        };
        break;

      case 'media':
        const mediaId = `M${nodes.filter(n => n.type === 'media').length + 1}`;
        newNode = {
          id: `media_${Date.now()}`,
          type: 'media',
          position,
          data: {
            ...NODE_DEFAULTS[NODE_TYPES.MEDIA],
            id: mediaId,
            stimulus_id: mediaId.toLowerCase(),
          }
        };
        break;

      case 'pleasant':
        const pleasantId = `P${nodes.filter(n => n.type === 'pleasant').length + 1}`;
        newNode = {
          id: `pleasant_${Date.now()}`,
          type: 'pleasant',
          position,
          data: {
            ...NODE_DEFAULTS[NODE_TYPES.PLEASANT],
            id: pleasantId,
          }
        };
        break;

      case 'file':
        const filePlaceholderId = `Q${nodes.filter(n => n.type === 'question').length + 1}`;
        const questionDefaults = NODE_DEFAULTS[NODE_TYPES.QUESTION];
        newNode = {
          id: `q_${Date.now()}`,
          type: 'question',
          position,
          data: {
            id: filePlaceholderId,
            text: 'Upload your file',
            type: 'file',
            variable: `q_${filePlaceholderId.toLowerCase()}`,
            required: true,
            options: [],
            clarificationPolicy: 'auto',
            accept: questionDefaults.accept,
            maxSizeMB: questionDefaults.maxSizeMB,
            storage: questionDefaults.storage,
            quarantine: questionDefaults.quarantine,
          }
        };
        break;

      default:
        console.warn(`Unknown node type: ${nodeType}`);
        return;
    }

    if (newNode) {
      onLoadSurvey({
        survey: surveyData,
        nodes: [...nodes, newNode],
        edges: edges
      });
    }
  };

  const duplicateSelectedNode = () => {
    if (!selectedNode) {
      showWarning('Select a node to duplicate');
      return;
    }
    try {
      const source = selectedNode;
      const duplicate = JSON.parse(JSON.stringify(source));

      // Generate a new unique id based on type
      let newId;
      if (source.type === 'start') newId = `start_${Date.now()}`;
      else if (source.type === 'end') newId = `end_${Date.now()}`;
      else if (source.type === 'decision') newId = `decision_${Date.now()}`;
      else if (source.type === 'media') newId = `media_${Date.now()}`;
      else if (source.type === 'pleasant') newId = `pleasant_${Date.now()}`;
      else if (source.type === 'package') newId = `pkg_${Date.now()}`;
      else newId = `q_${Date.now()}`;

      duplicate.id = newId;
      duplicate.position = {
        x: (source.position && typeof source.position.x === 'number' ? source.position.x : 0) + 32,
        y: (source.position && typeof source.position.y === 'number' ? source.position.y : 0) + 32
      };
      duplicate.data = { ...(source.data || {}) };

      if (source.type === 'question') {
        const nextIndex = nodes.filter(n => n.type === 'question').length + 1;
        const humanId = `Q${nextIndex}`;
        duplicate.data.id = humanId;
        if (duplicate.data.variable !== undefined) {
          duplicate.data.variable = `q_${humanId.toLowerCase()}`;
        }
      }

      if (source.type === 'decision') {
        duplicate.data.id = `D${nodes.filter(n => n.type === 'decision').length + 1}`;
      }

      if (source.type === 'end') {
        duplicate.data.id = newId;
        duplicate.data.question_id = newId;
      }

      if (source.type === 'media') {
        duplicate.data.id = `M${nodes.filter(n => n.type === 'media').length + 1}`;
      }

      if (source.type === 'pleasant') {
        duplicate.data.id = `P${nodes.filter(n => n.type === 'pleasant').length + 1}`;
      }

      if (source.type === 'package') {
        // review B4: an independent instance — deep-copy packageContent (the
        // shallow data spread above would otherwise share it) and assign a fresh
        // unique instance name (C2).
        duplicate.data.packageContent = source.data?.packageContent
          ? JSON.parse(JSON.stringify(source.data.packageContent))
          : duplicate.data.packageContent;
        duplicate.data.instanceName = suggestInstanceName(source.data?.title || source.data?.instanceName || 'package', nodes);
      }

      // Mark new selection
      const updatedNodes = nodes.map(n => ({ ...n, selected: false })).concat([{ ...duplicate, selected: true }]);

      onLoadSurvey({
        survey: surveyData,
        nodes: updatedNodes,
        edges
      });
      showSuccess('Node duplicated');
    } catch (err) {
      showError(`Duplicate failed: ${err.message}`);
    }
  };

  // ── Hand the save machinery up to App ──────────────────────────────────
  //
  // App owns the idle clock and has to rescue unsaved work at the 28-minute
  // warning, but the payload builder and the compile-and-save path live here.
  //
  // The ref is rewritten on every render and the exposed functions read through
  // it, so App gets one stable object that always calls the current versions.
  // Passing the raw functions would hand App a stale closure over old nodes and
  // edges — it would save the survey as it looked several renders ago.
  const saveApiRef = useRef({});
  saveApiRef.current.buildPayload = createSurveyData;
  saveApiRef.current.saveToCloud = handleSaveToCloud;

  useEffect(() => {
    if (!onExposeSaveApi) return undefined;
    onExposeSaveApi({
      buildPayload: (...args) => saveApiRef.current.buildPayload(...args),
      saveToCloud: (...args) => saveApiRef.current.saveToCloud(...args),
    });
    return () => onExposeSaveApi(null);
  }, [onExposeSaveApi]);

  return (
    <div className="toolbar">


      {/* LOCAL MODE NOTICE (commented out 2025-10-03): file management warning retained for potential re-enable */}
      {/*
      {!isSupported && (
        <div className="toolbar-section" style={{ backgroundColor: '#fff3cd', border: '1px solid #ffeaa7', borderRadius: '4px', padding: '8px' }}>
          <AlertTriangle size={16} color="#856404" />
          <span style={{ color: '#856404', fontSize: '12px', marginLeft: '5px' }}>
            Requires Chrome or Edge browser for file management
          </span>
        </div>
      )}
      */}

      <div className="toolbar-section">
        <button onClick={handleNewSurvey} title="New Survey">
          <FileText size={16} />
          New
        </button>

        <button onClick={handleLoad} title={isCloudMode ? 'Open (Cloud DB)' : 'Load Survey'} disabled={!isCloudMode && !isSupported}>
          <FolderOpen size={16} />
          {isCloudMode ? 'Open' : 'Load'}
        </button>

        {isCloudMode && currentSurveyId ? (
          // Save options for existing surveys in cloud mode
          <div style={{ display: 'flex', gap: '4px', alignItems: 'center' }}>
            {/* Primary save button - defaults to overwrite */}
            <button
              onClick={() => {
                if (isSaving) return;
                setSaveMode('overwrite');
                handleSaveToCloud({ mode: 'overwrite' });
              }}
              title="Save (Overwrite Current Version)"
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '4px',
                backgroundColor: '#5B9FFF',
                color: 'white',
                border: '1px solid #3D85E6',
                borderRadius: '4px',
                padding: '6px 8px',
                cursor: 'pointer'
              }}
              disabled={isSaving}
            >
              <Save size={16} />
              {isSaving ? 'Saving…' : 'Save Overwrite'}
            </button>

            {/* Dropdown for additional options */}
            <div ref={dropdownRef} style={{ position: 'relative', display: 'inline-block' }}>
              <button
                onClick={() => setShowSaveDropdown(!showSaveDropdown)}
                title="More Save Options"
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '2px',
                  backgroundColor: 'rgba(255, 255, 255, 0.15)',
                  color: 'white',
                  border: '1px solid rgba(255, 255, 255, 0.2)',
                  borderRadius: '4px',
                  padding: '6px 4px',
                  cursor: 'pointer',
                  minWidth: '24px'
                }}
                disabled={isSaving}
              >
                <ChevronDown size={14} />
              </button>

              {showSaveDropdown && (
                <div style={{
                  position: 'absolute',
                  top: '100%',
                  right: 0,
                  backgroundColor: 'white',
                  border: '1px solid #E8EAED',
                  borderRadius: '4px',
                  boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
                  zIndex: 1000,
                  minWidth: '160px'
                }}>
                  <button
                    onClick={() => {
                      if (isSaving) return;
                      setShowSaveDropdown(false);
                      handleSaveToCloud({ mode: 'version' });
                    }}
                    title="Save as a new version (keeps the current version intact)"
                    style={{
                      display: 'block',
                      width: '100%',
                      padding: '8px 12px',
                      border: 'none',
                      backgroundColor: 'white',
                      cursor: 'pointer',
                      textAlign: 'left',
                      fontSize: '14px'
                    }}
                    disabled={isSaving}
                  >
                    {isSaving ? 'Saving…' : 'Save New Version'}
                  </button>
                  <button
                    onClick={handleSaveAsPackage}
                    title="Save the current graph to the package library"
                    style={{
                      display: 'flex', alignItems: 'center', gap: 6,
                      width: '100%', padding: '8px 12px', border: 'none',
                      borderTop: '1px solid #F0F2F4', backgroundColor: 'white',
                      cursor: 'pointer', textAlign: 'left', fontSize: '14px'
                    }}
                    disabled={isSaving}
                  >
                    <Package size={14} /> Save as Package…
                  </button>
                </div>
              )}
            </div>
          </div>
        ) : (
          // Regular save button for new surveys or local mode (with a small
          // split for Save as Package so a never-saved graph can become one).
          <div style={{ display: 'flex', gap: 4, alignItems: 'center' }}>
            <button
              onClick={handleSave}
              title={isCloudMode ? 'Save (Cloud DB)' : (currentFileHandle ? 'Save' : 'Save As')}
              disabled={isCloudMode ? isSaving : !isSupported}
            >
              <Save size={16} />
              {isCloudMode && isSaving ? 'Saving…' : 'Save'}
            </button>
            <button
              onClick={handleSaveAsPackage}
              title="Save the current graph to the package library"
              disabled={isSaving}
            >
              <Package size={16} />
              Save as Package
            </button>
          </div>
        )}

        {/* LOCAL MODE Save As (commented out 2025-10-03) */}
        {/*
        {!isCloudMode && (
          <button onClick={handleSaveAs} title="Save As" disabled={!isSupported}>
            <Save size={16} />
            Save As
          </button>
        )}
        */}

        <button
          onClick={onDeleteSelection}
          title={selectionCount > 1 ? `Delete ${selectionCount} selected items` : 'Delete selected item'}
          disabled={!(selectionCount || selectedNode || selectedEdge)}
          style={{ color: '#d9534f' }}
        >
          <Trash2 size={16} />
          {selectionCount > 1 ? `Delete (${selectionCount})` : 'Delete'}
        </button>

      </div>

      {/* Cloud Open Dialog */}
      {isCloudMode && (
        <CloudTableDialog
          open={openCloudDialog}
          onClose={() => setOpenCloudDialog(false)}
          title=""
          columns={[
            { key: 'name', header: 'Name', width: '30%' },
            { key: 'id', header: 'Survey ID', width: '38%' },
            { key: 'version', header: 'Version', width: '10%', render: (r) => `v${r.version || '?'}` },
            { key: 'updated_at', header: 'Last Updated', width: '22%', render: (r) => (r.updated_at ? new Date(r.updated_at).toLocaleString() : '') }
          ]}
          fetchPage={({ limit, offset, search }) => listSurveysFromCloud({ limit, offset, search })}
          onDuplicate={async (row) => {
            try {
              const copy = await copySurveyInCloud({ surveyId: row.id });
              const copyName = copy?.name || copy?.title || `Copy of ${row.name || row.id}`;
              showSuccess(`Duplicated as "${copyName}"`);
              // Returning undefined lets CloudTableDialog refetch so the
              // new "Copy of …" entry appears in the list. The user can
              // then select it and Open or Rename without touching the
              // original.
            } catch (err) {
              showError(`Duplicate failed: ${err.message}`);
              return false; // Skip the refetch on error
            }
          }}
          onPick={async (row) => {
            try {
              const latestVer = row.version || 1;
              const { data, etag } = await getSurveyVersionFromCloud(row.id, latestVer);
              // data is the sam-dbam envelope {data: {version_obj}, meta: {...}}
              const versionData = data?.data || data || {};
              let payload = versionData.designer_payload_json;
              if (!payload || typeof payload !== 'object') {
                // Legacy survey: reconstruct from descriptor_json
                payload = reconstructPayloadFromDescriptor(versionData.descriptor_json, row.name);
                if (!payload) {
                  throw new Error('designer_payload_json is missing and descriptor cannot be reconstructed');
                }
              }
              const survey = payload.survey || {};
              const nodes = Array.isArray(payload.nodes) ? payload.nodes : [];
              const edges = Array.isArray(payload.edges) ? payload.edges : [];

              // Store survey metadata for future saves
              setCurrentSurveyId(row.id);
              setCurrentVersionNumber(latestVer);
              setCurrentEtag(etag);
              setCurrentSurveyName(row.name || null);
              setSaveMode('overwrite'); // Default to overwrite when loading existing survey

              onLoadSurvey({ survey, nodes, edges });
              setOpenCloudDialog(false);
              showSuccess(`Opened ${row.name || row.id} v${latestVer}`);
            } catch (err) {
              showError(`Open failed: ${err.message}`);
            }
          }}
        />
      )}

      {/* Save as Package — metadata modal (WP-5) */}
      <PackageMetadataModal
        open={showPackageModal}
        onClose={() => setShowPackageModal(false)}
        onSubmit={handleConfirmSavePackage}
        initial={{ title: surveyData.title || '', description: surveyData.description || '' }}
        computed={packageModalCtx.computed}
        validation={packageModalCtx.validation}
        busy={packageSaving}
      />

      {/* Add Node → Package — binding picker (WP-6); also serves rebind */}
      <PackagePanel
        open={showPackagePicker}
        mode="picker"
        onClose={() => { setShowPackagePicker(false); setRebindTargetNodeId(null); }}
        onPick={(row) => (rebindTargetNodeId ? handleRebindPackage(rebindTargetNodeId, row) : handleBindPackage(row))}
      />

      {/* Packages command — management mode (WP-7) */}
      <PackagePanel
        open={showPackageManager}
        mode="management"
        reloadSignal={pkgReload}
        onClose={() => setShowPackageManager(false)}
        onOpenForEdit={handlePackageOpenForEdit}
        onDelete={handlePackageDelete}
        onEditMeta={handlePackageEditMeta}
        onExport={handlePackageExport}
        onImport={handlePackageImport}
      />

      {/* Package tag/metadata edit (WP-7) */}
      <PackageMetadataModal
        open={showEditMeta}
        mode="edit"
        onClose={() => setShowEditMeta(false)}
        onSubmit={handleConfirmEditMeta}
        initial={{ title: editMetaRow?.title || editMetaRow?.name || '', description: editMetaRow?.description || '' }}
        initialTags={Array.isArray(editMetaRow?.tags) ? editMetaRow.tags : []}
        busy={packageSaving}
      />

      {/* LOCAL MODE file name banner (commented out 2025-10-03) */}
      {/*
      {currentFileName && (
        <div className="toolbar-section" style={{ fontSize: '12px', color: '#666' }}>
          <span>File: {currentFileName}</span>
        </div>
      )}
      */}

      {isCloudMode && currentSurveyId && (
        <div className="toolbar-section" style={{ fontSize: '12px', color: 'rgba(255, 255, 255, 0.7)' }}>
          <span>Editing: {surveyData.title || 'Survey'} v{currentVersionNumber}</span>
        </div>
      )}

      <div className="toolbar-section">
        <button onClick={handleExport} title="Export to CloudSam Format">
          <Download size={16} />
          Export
        </button>
        {!isCloudMode && (
          <button onClick={handleSaveToCloud} title="Save to Cloud DB" disabled={isSaving}>
            <Save size={16} />
            {isSaving ? 'Saving…' : 'Save to Cloud DB'}
          </button>
        )}
      </div>

      <div className="toolbar-section" style={{ position: 'relative' }}>
        <button
          onClick={() => setShowAddNodeMenu(prev => !prev)}
          title="Add Node"
          data-testid="add-node-button"
        >
          <Plus size={16} />
          Add Node
          <ChevronDown size={12} style={{ marginLeft: 4 }} />
        </button>
        {showAddNodeMenu && (
          <div className="add-node-dropdown" onMouseLeave={() => setShowAddNodeMenu(false)}>
            <button onClick={() => { addQuickNode('start'); setShowAddNodeMenu(false); }} title="Add a Start node">
              <Play size={14} /> Start
            </button>
            <button onClick={() => { addQuickNode('question'); setShowAddNodeMenu(false); }} title="Add a Question node">
              <MessageSquare size={14} /> Question
            </button>
            <button onClick={() => { addQuickNode('end'); setShowAddNodeMenu(false); }} title="Add an End node">
              <Square size={14} /> End
            </button>
            <button onClick={() => { addQuickNode('decision'); setShowAddNodeMenu(false); }} title="Add a Decision (branching) node">
              <Diamond size={14} /> Decision
            </button>
            <button onClick={() => { addQuickNode('media'); setShowAddNodeMenu(false); }} title="Add a Media (image/video) node">
              <Image size={14} /> Media
            </button>
            <button onClick={() => { addQuickNode('pleasant'); setShowAddNodeMenu(false); }} title="Add a Pleasant (AI joke or pleasantry) node">
              <Smile size={14} /> Pleasant
            </button>
            <button onClick={() => { addQuickNode('file'); setShowAddNodeMenu(false); }} title="Add a File Upload question">
              <Upload size={14} /> File Upload
            </button>
            <button onClick={() => { setShowAddNodeMenu(false); setShowPackagePicker(true); }} title="Insert a library package as a bound node">
              <Package size={14} /> Package
            </button>
          </div>
        )}
      </div>

      <div className="toolbar-section">
        <button onClick={() => setShowPackageManager(true)} title="Manage packages">
          <Package size={16} />
          Packages
        </button>
        <button onClick={duplicateSelectedNode} title="Duplicate selected node">
          <Copy size={16} />
          Duplicate
        </button>
        <button
          onClick={handleValidate}
          className="secondary"
          title="Validate Survey Flow"
        >
          <AlertTriangle size={16} />
          Validate
        </button>


      </div>

      <div className="toolbar-section">
        <input
          type="text"
          value={surveyData.title || ''}
          onChange={(e) => onSurveyDataChange({ title: e.target.value })}
          placeholder="Survey Title"
          style={{ minWidth: '200px' }}
        />
      </div>

      {onToggleAssistant && (
        <div className="toolbar-section">
          <button onClick={onToggleAssistant} title="Open Survey Assistant">
            <Wand2 size={16} />
            Survey Assistant
          </button>
        </div>
      )}


    </div >
  );
};

export default Toolbar;
